import { Fragment, type ReactNode } from "react";

const ACCENT_PATTERN = /<em>([\s\S]*?)<\/em>/g;

type RichTextProps = {
  /** A dictionary string. `\n` becomes a break, `<em>…</em>` becomes the accent run. */
  text: string;
  /** Class applied to the accent run. */
  accentClassName?: string;
  /**
   * Class applied to each `<br>`. Most section headings pass
   * `"hidden sm:block"` so the break only splits on wide screens and phones
   * wrap naturally.
   */
  breakClassName?: string;
  /** Wraps the accent run in something other than a span — e.g. `"strong"`. */
  accentAs?: "span" | "strong" | "em";
};

/**
 * Renders the two bits of markup the dictionary strings are allowed to carry.
 *
 * Headings on this site are two-tone: half in body colour, half in the brand
 * accent. Splitting that into separate `before` / `accent` / `after` keys
 * bakes ENGLISH WORD ORDER into the data — Hindi puts the verb last, so
 * "Running a school shouldn't feel like [firefighting]" becomes
 * "स्कूल चलाना [आग बुझाने जैसा] नहीं होना चाहिए", with the accent in the
 * middle and a trailing clause English doesn't have. An inline marker lets
 * each language place its own accent and keeps the component free of layout
 * assumptions about either.
 *
 * Deliberately not `dangerouslySetInnerHTML`: the input is static translator
 * copy today, but parsing to React nodes means it stays inert even if a
 * string ever comes from a CMS, and `<em>` remains the only markup that
 * does anything.
 */
export default function RichText({
  text,
  accentClassName = "section-heading-accent",
  breakClassName,
  accentAs = "span",
}: RichTextProps) {
  const Accent = accentAs;

  return (
    <>
      {text.split("\n").map((line, lineIdx) => {
        const parts: ReactNode[] = [];
        let cursor = 0;

        // `matchAll` over a /g regex gives index + capture, which is all the
        // splitting needs — no stateful `lastIndex` to reset between lines.
        for (const match of line.matchAll(ACCENT_PATTERN)) {
          const at = match.index ?? 0;
          if (at > cursor) parts.push(line.slice(cursor, at));
          parts.push(
            <Accent key={`a-${lineIdx}-${at}`} className={accentClassName}>
              {match[1]}
            </Accent>
          );
          cursor = at + match[0].length;
        }
        if (cursor < line.length) parts.push(line.slice(cursor));

        return (
          <Fragment key={lineIdx}>
            {/* The space before the break is load-bearing. Most callers pass a
                responsive `breakClassName` ("hidden sm:block"), so at the other
                breakpoint the <br> is display:none and the two lines run
                together as one — without this they'd collide into
                "Got Questions?We've got answers." The JSX these strings
                replaced had exactly this space sitting before its own <br>.
                When the break IS shown the space falls at the end of a line
                and collapses to nothing. */}
            {lineIdx > 0 && (
              <>
                {" "}
                <br className={breakClassName} />
              </>
            )}
            {parts}
          </Fragment>
        );
      })}
    </>
  );
}
